import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../hooks/hook";
import { dataFromApi } from "../../redux/getApiData/reducer";
import { getIdToken } from "../../helpers/msal_auth";
import { authScopes, protectedResources } from "../../authConfig";
import { callApi } from "../../helpers/call_api"; // Make sure axios is installed
import "../../styles/css/spinner.css";
import brand from "../../brand";

export const AdminCollectorsPage = () => {
  const { t } = useTranslation();
  const apiData = useAppSelector(dataFromApi);
  const [collectors, setCollectors] = useState([]);
  const [etlErrorTracking, setEtlErrorTracking] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentCollector, setCurrentCollector] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCollectors = async () => {
      try {
        setIsLoading(true);
        let idToken = await getIdToken(authScopes);
        let response = await callApi(
          idToken,
          protectedResources.supportApi + "/admin/collector",
          "GET"
        );
        setCollectors(response?.collector_list || []);
        setEtlErrorTracking(response?.etl_error_tracking || []);
        setError(null);
      } catch (err) {
        console.error("Error fetching collectors:", err);
        setError(err.message || "Failed to fetch collectors");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCollectors();
  }, []);

  const handleOpenDialog = (collector = null) => {
    setCurrentCollector(collector);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentCollector(null);
  };

  const handleSaveCollector = () => {
    // Logic to save collector would go here
    handleCloseDialog();
  };

  // Function to determine status color and styling
  const getStatusChipProps = (status) => {
    const statusLower = status?.toLowerCase() || "";

    if (statusLower.includes("success")) {
      return {
        label: t("adminCollectorsSuccess"),
        color: "success",
        sx: { fontWeight: "bold" },
      };
    } else if (statusLower.includes("fail") || statusLower.includes("error")) {
      return {
        label: t("adminCollectorsFailed"),
        color: "error",
        sx: { fontWeight: "bold" },
      };
    } else {
      return {
        label: status || t("unknown"),
        color: "default",
        variant: "outlined",
      };
    }
  };

  if (isLoading) {
    return (
      <div className="spinner">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography variant="subtitle2" color="error">
          {t("adminCollectorsError")}: {error}
        </Typography>
      </Box>
    );
  }

return (
    <Box p={3} sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        alignItems="center"
        sx={{ marginBottom: 7, flexShrink: 0, paddingTop: 2}}
      >
        <Typography
          className="secondaryColor"
          style={{
            fontFamily: brand.titleFont,
            fontSize: 24,
            fontWeight: 700,
            lineHeight: "36px",
          }}
        >
          {t("collectorsPageTitle")}
        </Typography>
      </Stack>
      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          flexGrow: 1,
          marginBottom: 4
        }}
      >
        <Table sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography
                  className="textColor"
                  sx={{
                    fontSize: "18px",
                    fontFamily: brand.titleFont,
                    fontWeight: "400",
                    textAlign: "left",
                  }}
                >
                  {t("adminCollectorsColumnCollector")}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  className="textColor"
                  sx={{
                    fontSize: "18px",
                    fontFamily: brand.titleFont,
                    fontWeight: "400",
                    textAlign: "left",
                  }}
                >
                  {t("adminCollectorsColumnEnabled")}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  className="textColor"
                  sx={{
                    fontSize: "18px",
                    fontFamily: brand.titleFont,
                    fontWeight: "400",
                    textAlign: "left",
                  }}
                >
                  {t("adminCollectorsColumnLastRun")}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  className="textColor"
                  sx={{
                    fontSize: "18px",
                    fontFamily: brand.titleFont,
                    fontWeight: "400",
                    textAlign: "left",
                  }}
                >
                  {t("adminCollectorsColumnLastRunStatus")}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  className="textColor"
                  sx={{
                    fontSize: "18px",
                    fontFamily: brand.titleFont,
                    fontWeight: "400",
                    textAlign: "left",
                  }}
                >
                  {t("adminCollectorsColumnNextRun")}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  className="textColor"
                  sx={{
                    fontSize: "18px",
                    fontFamily: brand.titleFont,
                    fontWeight: "400",
                    textAlign: "left",
                  }}
                >
                  {t("adminCollectorsSchedule")}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {collectors.map((collector, index) => (
              <TableRow key={`${collector.collector_name}-${index}`}>
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontFamily: brand.bodyFont,
                      fontWeight: "400",
                      textAlign: "left",
                    }}
                  >
                    {collector.collector_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontFamily: brand.bodyFont,
                      fontWeight: "400",
                      textAlign: "left",
                    }}
                  >
                    {collector.enabled
                      ? t("adminCollectorsActive")
                      : t("adminCollectorsInactive")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontFamily: brand.bodyFont,
                      fontWeight: "400",
                      textAlign: "left",
                    }}
                  >
                    {collector.last_run}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    {...getStatusChipProps(collector.last_run_status)}
                    size="small"
                    sx={{
                      ...getStatusChipProps(collector.last_run_status).sx,
                      fontSize: "14px",
                      minWidth: "80px",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontFamily: brand.bodyFont,
                      fontWeight: "400",
                      textAlign: "left",
                    }}
                  >
                    {collector.next_run}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontFamily: brand.bodyFont,
                      fontWeight: "400",
                      textAlign: "left",
                    }}
                  >
                    {collector.scheduled}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
        <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        alignItems="center"
        sx={{ marginBottom: 7, flexShrink: 0, paddingTop: 2}}
      >
        <Typography
          className="secondaryColor"
          style={{
            fontFamily: brand.titleFont,
            fontSize: 24,
            fontWeight: 700,
            lineHeight: "36px",
          }}
        >
          {t("collectorsEtlPageTitle")}
        </Typography>
      </Stack>
      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          flexGrow: 1
        }}
      >
        <Table sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography
                  className="textColor"
                  sx={{
                    fontSize: "18px",
                    fontFamily: brand.titleFont,
                    fontWeight: "400",
                    textAlign: "left",
                  }}
                >
                  {t("ETLErrorTimeStamp")}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  className="textColor"
                  sx={{
                    fontSize: "18px",
                    fontFamily: brand.titleFont,
                    fontWeight: "400",
                    textAlign: "left",
                  }}
                >
                  {t("CollectorType")}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  className="textColor"
                  sx={{
                    fontSize: "18px",
                    fontFamily: brand.titleFont,
                    fontWeight: "400",
                    textAlign: "left",
                  }}
                >
                  {t("MessageS3Key")}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  className="textColor"
                  sx={{
                    fontSize: "18px",
                    fontFamily: brand.titleFont,
                    fontWeight: "400",
                    textAlign: "left",
                  }}
                >
                  {t("ErrorDetail")}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {etlErrorTracking
                .map((error) => {
                    const combined = error['s3key-error_dt'];

                    const lastHyphenIndex = combined.lastIndexOf('-');
                    const secondLastHyphenIndex = combined.lastIndexOf('-', lastHyphenIndex - 1);

                    const s3key = combined.substring(0, secondLastHyphenIndex);
                    const timestamp = combined.substring(secondLastHyphenIndex + 1);

                    const [timestampDate, timestampTime] = timestamp.split('-');
                    const year = timestampDate.substring(0, 4);
                    const month = timestampDate.substring(4, 6);
                    const day = timestampDate.substring(6, 8);
                    const hour = timestampTime.substring(0, 2);
                    const minute = timestampTime.substring(2, 4);
                    const second = timestampTime.substring(4, 6);
                    const formattedTimestamp = `${year}-${month}-${day} ${hour}:${minute}:${second}`;

                    return {
                      ...error,
                      s3key,
                      error_timestamp: formattedTimestamp
                    };
                })
                .sort((a, b) => b.error_timestamp.localeCompare(a.error_timestamp))
                .map((error, index) => (
              <TableRow key={`${error.s3key}-${index}`}>
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontFamily: brand.bodyFont,
                      fontWeight: "400",
                      textAlign: "left",
                    }}
                  >
                    {error.error_timestamp}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontFamily: brand.bodyFont,
                      fontWeight: "400",
                      textAlign: "left",
                    }}
                  >
                    {error.collector_type}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontFamily: brand.bodyFont,
                      fontWeight: "400",
                      textAlign: "left",
                    }}
                  >
                    {error.s3key}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontFamily: brand.bodyFont,
                      fontWeight: "400",
                      textAlign: "left",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        whiteSpace: "normal",
                        overflow: "visible",
                        textOverflow: "unset",
                      },
                    }}
                  >
                    {error.error_detail}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};